import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, Mail, MessageSquare } from "lucide-react";
import { formatDate } from "@/lib/utils-cart";
import { StatusPill } from "@/components/Badges";

function Campaigns({ mode }) {
  // mode = 'email' or 'sms'
  const isEmail = mode === "email";
  const endpoint = isEmail ? "/campaigns/email" : "/campaigns/sms";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = isEmail ? { name: "", subject: "", body: "" } : { name: "", message: "" };
  const [form, setForm] = useState(empty);

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [mode]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      toast.success("Campaign saved as draft");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error("Failed"); }
  };

  const send = async (id) => {
    if (!confirm(`Send this ${mode} campaign now? (MOCK send for MVP)`)) return;
    const { data } = await api.post(`${endpoint}/${id}/send`);
    toast.success(`${mode === "email" ? "Email" : "SMS"} queued to ${data.sent} contacts (MOCKED)`);
    load();
  };

  return (
    <DashboardLayout
      title={isEmail ? "Email Campaigns" : "SMS Campaigns"}
      subtitle={`${isEmail ? "Drip" : "SMS"} broadcasts to your customer base. Provider integration is MOCKED — UI flow is fully functional.`}
      actions={<button onClick={() => setOpen(!open)} className="v-btn-primary" data-testid={`campaign-${mode}-add`}><Plus size={14} /> New {mode}</button>}
    >
      {open && (
        <form onSubmit={create} className="v-card mb-4 space-y-3">
          <input className="v-input" placeholder="Campaign name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid={`campaign-${mode}-name`} />
          {isEmail ? (
            <>
              <input className="v-input" placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} required data-testid="email-subject" />
              <textarea rows={5} className="v-input" placeholder="Email body" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} required data-testid="email-body" />
            </>
          ) : (
            <textarea rows={3} className="v-input" placeholder="SMS message (160 chars)" maxLength={160} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required data-testid="sms-message" />
          )}
          <div className="flex gap-2">
            <button className="v-btn-primary" data-testid={`campaign-${mode}-save`}>Save draft</button>
            <button type="button" className="v-btn-outline" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="v-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="p-3">Campaign</th>
              <th className="p-3">{isEmail ? "Subject" : "Message"}</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Sent</th>
              <th className="p-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0">
                <td className="p-3 font-medium text-primary">{c.name}</td>
                <td className="p-3 text-muted-foreground text-xs line-clamp-1 max-w-xs">{isEmail ? c.subject : c.message}</td>
                <td className="p-3">
                  <StatusPill tone={c.status === "sent" ? "success" : "default"}>{c.status}</StatusPill>
                  {c.is_mock && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-amber-600">MOCKED</span>}
                </td>
                <td className="p-3 text-right font-mono">{c.sent_count || 0}</td>
                <td className="p-3 text-xs">{formatDate(c.created_at)}</td>
                <td className="p-3 text-right">
                  {c.status !== "sent" && (
                    <button onClick={() => send(c.id)} className="v-btn-outline text-xs" data-testid={`campaign-${mode}-send-${c.id}`}>
                      <Send size={12} /> Send
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function EmailCampaigns() { return <Campaigns mode="email" />; }
export function SMSCampaigns() { return <Campaigns mode="sms" />; }
